/*
 * Lazy-loads the contents of the mobile menu drawer.
 *
 * The <details>/<summary> shell stays in the initial HTML so that
 * MenuDrawer's constructor in global.js finds what it expects and
 * open/close works from the first paint. Only the inner container
 * is fetched, on first interaction.
 *
 * Anything bound at page load in global.js does NOT apply to markup
 * injected later, so the bindings below are re-applied by hand,
 * scoped to the injected subtree only. Scoping matters: #menu-drawer
 * does not contain the main <summary>, so re-binding here cannot
 * double-bind the main toggle (which would open and immediately
 * close the drawer).
 */
(function () {
  var drawer = document.querySelector('header-drawer[data-lazy-src]');
  if (!drawer) return;

  var container = drawer.querySelector('#menu-drawer');
  var summary = drawer.querySelector('#Details-menu-drawer-container > summary');
  if (!container || !summary) return;

  var state = 'idle';

  /* Mirrors the [id^="Details-"] summary loop in global.js.
     The keyup/Escape binding is deliberately omitted: global.js skips it
     for anything inside header-drawer, and MenuDrawer.onKeyUp handles it. */
  function applyDetailsAria(root) {
    root.querySelectorAll('[id^="Details-"] summary').forEach(function (s) {
      s.setAttribute('role', 'button');
      s.setAttribute('aria-expanded', s.parentNode.hasAttribute('open'));

      if (s.nextElementSibling && s.nextElementSibling.getAttribute('id')) {
        s.setAttribute('aria-controls', s.nextElementSibling.id);
      }

      s.addEventListener('click', function (event) {
        event.currentTarget.setAttribute(
          'aria-expanded',
          !event.currentTarget.closest('details').hasAttribute('open')
        );
      });
    });
  }

  /* Mirrors MenuDrawer.bindEvents(), but only for the injected subtree.
     Calling drawer.bindEvents() directly would re-bind the main summary. */
  function bindDrawerEvents(root) {
    root.querySelectorAll('summary').forEach(function (s) {
      s.addEventListener('click', drawer.onSummaryClick.bind(drawer));
    });

    root
      .querySelectorAll('button:not(.localization-selector)')
      .forEach(function (b) {
        b.addEventListener('click', drawer.onCloseButtonClick.bind(drawer));
      });
  }

  /* Mirrors the .country_picker / .country_list handlers in global.js,
     which run once at page load and therefore miss injected markup. */
  function bindCountryPicker(root) {
    root.querySelectorAll('.country_picker').forEach(function (btn) {
      btn.addEventListener('click', function (event) {
        var parent = this.closest('.country_parent');
        if (!parent) return;

        var dropdown = parent.querySelector('.country_list');
        if (dropdown) dropdown.classList.toggle('tw-hidden');

        event.stopPropagation();
      });
    });

    root.querySelectorAll('.country_list').forEach(function (list) {
      list.addEventListener('mouseleave', function () {
        this.classList.add('tw-hidden');
      });

      document.addEventListener('click', function (event) {
        if (!list.contains(event.target)) list.classList.add('tw-hidden');
      });
    });
  }

  function load() {
    if (state !== 'idle') return;
    state = 'loading';

    fetch(drawer.dataset.lazySrc)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var inner = doc.querySelector('.menu-drawer__inner-container');
        if (!inner) throw new Error('menu-drawer__inner-container not found');

        container.appendChild(inner);
        state = 'loaded';

        applyDetailsAria(inner);
        bindDrawerEvents(inner);
        bindCountryPicker(inner);
      })
      .catch(function (error) {
        /* Leave the drawer empty rather than half-built, and allow a retry
           on the next interaction. */
        state = 'idle';
        console.error('Lazy menu drawer failed to load:', error);
      });
  }

  /* Prefetch on intent so the drawer is usually already filled by the
     time the open animation finishes. */
  summary.addEventListener('pointerenter', load);
  summary.addEventListener('touchstart', load, { passive: true });
  summary.addEventListener('click', load);
})();
